import { useState, useEffect, useMemo } from "react";
import {
    Grid2,
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    Checkbox,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
} from "@mui/material";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import {
    GetAuditLogSearchUrl,
    GetAuditLogExcel,
    getFillListUrl,
    auditEventActionID,
} from "../../utils/constants";
import { axiosGet } from "../../utils/Axios";
import CommonPagination from "../../components/CommonPagination";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/toast";
import moment from "moment";
import { HeaderBlock } from "@colliers/colliers-b2c";
import axios from "axios";

const FILTER_INPUT_CLASS = "padding-top-s list-page-control-client";
const FILTER_SELECT_CLASS = "padding-top-s list-page-control-client text-align-left";
const LIST_ITEMS_PER_PAGE = 10;
const DATE_FORMAT_DMY = "DD-MM-YYYY HH:mm:ss";
const DATE_FORMAT_DISPLAY = "DD MMM YYYY HH:mm:ss";
const DATE_FORMAT_YMD = "YYYY-MM-DD HH:mm:ss";
const AUDIT_LOG_FETCH_ERROR = "Failed to fetch Audit Log: No data found.";
const AUDIT_LOG_EXCEL_ERROR = "Failed to generate excel";

const AUDIT_LOG_COLUMNS = [
    { id: "eventName", labelKey: "auditLog.event" },
    { id: "details", labelKey: "auditLog.details" },
    { id: "changedOn", labelKey: "auditLog.changedOn" },
    { id: "changedBy", labelKey: "auditLog.changedBy" },
    { id: "userType", labelKey: "auditLog.userType" },
    { id: "dashboard", labelKey: "auditLog.dashboard" },
    { id: "group", labelKey: "auditLog.group" },
];

export const parseChangedOnDate = (value) => {
    if (!value || typeof value !== "string") return null;
    const trimmed = value.replace(/^GMT\s+/i, "").trim();
    const parsed = moment.utc(
        trimmed,
        [DATE_FORMAT_DMY, DATE_FORMAT_DISPLAY, DATE_FORMAT_YMD, moment.ISO_8601],
        true
    );
    if (parsed.isValid()) {
        return parsed.valueOf();
    }
    const fallback = moment(trimmed, DATE_FORMAT_DMY);
    return fallback.isValid() ? fallback.valueOf() : null;
};

export const formatChangedOnDate = (changedOnValue, invalidDateLabel) => {
    if (!changedOnValue) return invalidDateLabel;
    const trimmed = String(changedOnValue).replace(/^GMT\s+/i, "").trim();
    const parsed = moment(trimmed, [DATE_FORMAT_DMY, DATE_FORMAT_DISPLAY, moment.ISO_8601]);
    return parsed.isValid() ? parsed.format(DATE_FORMAT_DISPLAY) : invalidDateLabel;
};

export const buildFormattedDateRange = (fromDate, toDate) => {
    const trimmedFrom = fromDate?.trim() ?? "";
    const trimmedTo = toDate?.trim() ?? "";
    if (trimmedFrom && trimmedTo) {
        return `${trimmedFrom}|${trimmedTo}`;
    }
    if (trimmedFrom) {
        return `greater than ${trimmedFrom}`;
    }
    if (trimmedTo) {
        return `less than ${trimmedTo}`;
    }
    return "";
};

export const buildEventIDParam = (selectedEvents) => {
    if (!Array.isArray(selectedEvents) || selectedEvents.length === 0) {
        return null;
    }
    return selectedEvents.map(String).join(",");
};

const compareNullableDates = (timeA, timeB, direction) => {
    if (timeA === null && timeB === null) return 0;
    if (timeA === null) return 1;
    if (timeB === null) return -1;
    return (timeA - timeB) * direction;
};

const compareStrings = (valA, valB) =>
    String(valA ?? "")
        .trim()
        .localeCompare(String(valB ?? "").trim(), undefined, {
            sensitivity: "base",
        });

const compareByChangedOn = (a, b, direction) =>
    compareNullableDates(
        parseChangedOnDate(a?.changedOn),
        parseChangedOnDate(b?.changedOn),
        direction
    );

const compareAuditLogItems = (a, b, sortBy, direction) => {
    if (sortBy === "changedOn") {
        const dateComparison = compareByChangedOn(a, b, direction);
        if (dateComparison !== 0) {
            return dateComparison;
        }
        return compareStrings(a?.eventName, b?.eventName) || compareStrings(a?.details, b?.details);
    }

    const primaryComparison = compareStrings(a?.[sortBy], b?.[sortBy]) * direction;
    if (primaryComparison !== 0) {
        return primaryComparison;
    }

    const secondaryDateComparison = compareByChangedOn(a, b, -1);
    if (secondaryDateComparison !== 0) {
        return secondaryDateComparison;
    }

    return compareStrings(a?.eventName, b?.eventName) || compareStrings(a?.details, b?.details);
};

export const sortAuditLogList = (list, sortBy, sortOrder) => {
    if (!Array.isArray(list) || !sortBy) {
        return list || [];
    }

    const direction = sortOrder === "asc" ? 1 : -1;
    return [...list].sort((a, b) => compareAuditLogItems(a, b, sortBy, direction));
};

export const getEventCode = (item) => String(item?.code ?? item?.id ?? "");

export const getEventLabel = (item) => String(item?.description ?? item?.name ?? "");

export const extractDownloadFileName = (disposition) => {
    const defaultFileName = `CRD_Audit_Log_${moment().format("YYYY-MM-DD")}.xlsx`;
    if (!disposition) return defaultFileName;
    const filenameStarMatch = /filename\*=UTF-8''([^;]+)/.exec(disposition);
    if (filenameStarMatch?.[1]) {
        return decodeURIComponent(filenameStarMatch[1]);
    }
    const filenameMatch = /filename="?([^";]+)/.exec(disposition);
    return filenameMatch?.[1] || defaultFileName;
};

const triggerBlobDownload = (blobData, fileName) => {
    const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

const getAuditLogRowKey = (auditLog) =>
    [
        auditLog?.dashboardID ?? "",
        auditLog?.eventName ?? "",
        auditLog?.changedOn ?? "",
        auditLog?.changedBy ?? "",
        auditLog?.details ?? "",
    ].join("|");

const AuditLog = () => {
    const navigate = useNavigate();
    const { ToastComponent, showToast } = useToast();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [emailText, setEmailText] = useState("");
    const [eventList, setEventList] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: "",
        toDate: "",
        emailText: "",
        selectedEvents: [],
    });
    const [auditLogList, setAuditLogList] = useState([]);
    const [sortBy, setSortBy] = useState("changedOn");
    const [sortOrder, setSortOrder] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        setSortBy(property);
        setCurrentPage(1);
    };

    const sortedAuditLog = useMemo(
        () => sortAuditLogList(auditLogList, sortBy, sortOrder),
        [auditLogList, sortBy, sortOrder]
    );

    const startIndex = (currentPage - 1) * LIST_ITEMS_PER_PAGE;
    const paginatedAuditLog = sortedAuditLog.slice(startIndex, startIndex + LIST_ITEMS_PER_PAGE);
    const totalItems = sortedAuditLog.length;

    const getEventList = async () => {
        try {
            const response = await axiosGet(getFillListUrl, {
                actionID: auditEventActionID,
                valueID: null,
                keyValue: null,
            });
            if (Array.isArray(response)) {
                setEventList(response);
            }
        } catch (error) {
            console.error("Failed to fetch Audit Log events:", error);
            setEventList([]);
        }
    };

    const getAuditLogList = async (overrideFilters) => {
        setIsLoading(true);

        try {
            const activeFilters = overrideFilters ?? {
                fromDate,
                toDate,
                emailText,
                selectedEvents,
            };

            const validatedEmail = activeFilters.emailText?.trim() ?? "";
            const formattedDateRange = buildFormattedDateRange(
                activeFilters.fromDate,
                activeFilters.toDate
            );
            const eventIDParam = buildEventIDParam(activeFilters.selectedEvents);

            const response = await axiosGet(GetAuditLogSearchUrl, {
                dateRange: formattedDateRange,
                email: validatedEmail,
                eventID: eventIDParam,
            });

            if (response) {
                setAppliedFilters(activeFilters);
                setAuditLogList(Array.isArray(response) ? response : []);
            } else {
                showToast("error", AUDIT_LOG_FETCH_ERROR);
            }
        } catch (error) {
            console.error("Failed to fetch Audit Log:", error);
            showToast("error", AUDIT_LOG_FETCH_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const userInfo = localStorage.getItem("userInfo");
        if (userInfo == null) {
            navigate("/login/KeyStone_Login");
            return;
        }
        void getEventList();
        void getAuditLogList();
    }, []);

    const searchBtnCall = async () => {
        setCurrentPage(1);
        await getAuditLogList();
    };

    const resetSearch = async () => {
        setFromDate("");
        setToDate("");
        setEmailText("");
        setSelectedEvents([]);
        setSortBy("changedOn");
        setSortOrder("desc");
        setCurrentPage(1);
        await getAuditLogList({
            fromDate: "",
            toDate: "",
            emailText: "",
            selectedEvents: [],
        });
    };

    const downloadBtnCall = async () => {
        setIsLoading(true);
        try {
            const validatedEmail = appliedFilters.emailText?.trim() ?? "";
            const formattedDateRange = buildFormattedDateRange(
                appliedFilters.fromDate,
                appliedFilters.toDate
            );
            const eventIDParam = buildEventIDParam(appliedFilters.selectedEvents);

            const response = await axios.get(GetAuditLogExcel, {
                params: {
                    dateRange: formattedDateRange,
                    email: validatedEmail,
                    eventID: eventIDParam,
                    sortBy,
                    sortOrder,
                },
                responseType: "blob",
            });
            if (response) {
                const fileName = extractDownloadFileName(response.headers?.["content-disposition"]);
                triggerBlobDownload(response.data, fileName);
            } else {
                showToast("error", AUDIT_LOG_EXCEL_ERROR);
            }
        } catch (error) {
            console.error("Failed to generate excel:", error);
            showToast("error", AUDIT_LOG_EXCEL_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFromDateChange = (event) => {
        setFromDate(event.target.value);
    };

    const handleToDateChange = (event) => {
        setToDate(event.target.value);
    };

    const handleEmailChange = (event) => {
        setEmailText(event.target.value);
    };

    const handleEventChange = (event) => {
        const { value } = event.target;
        const normalized = typeof value === "string" ? value.split(",").filter(Boolean) : value;
        setSelectedEvents(normalized.map(String));
    };

    const renderSelectedEvents = (selected) => {
        if (!Array.isArray(selected) || selected.length === 0) {
            return t("auditLog.selectEvent");
        }
        return selected
            .map((code) => {
                const match = eventList.find((item) => getEventCode(item) === String(code));
                return match ? getEventLabel(match) : String(code);
            })
            .filter(Boolean)
            .join(", ");
    };

    return (
        <>
            {isLoading && <Loader />}
            <HeaderBlock
                left=""
                title={t("auditLog.auditLog")}
                leftCenterAlign="true"
                actionsCenterAlign="true"
            />

            <Box
                id="main"
                className="full-width padding-right-m padding-left-m padding-bottom-m padding-top-none auditLogMain"
            >
                <Box className="pagetitle main-area-top">
                    <section className="section page-body">
                        <Box>
                            {/* Search Filters */}
                            <Grid2
                                container
                                spacing={2}
                                alignItems="flex-end"
                                className="margin-top-base client-filter-bar audit-filter-bar"
                            >
                                {/* From Date Input */}
                                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_INPUT_CLASS}
                                        label={t("auditLog.fromDate")}
                                        type="date"
                                        variant="outlined"
                                        value={fromDate}
                                        onChange={handleFromDateChange}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            htmlInput: {
                                                max: toDate || undefined,
                                                "aria-label": t("auditLog.fromDate"),
                                            },
                                        }}
                                    />
                                </Grid2>

                                {/* To Date Input */}
                                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_INPUT_CLASS}
                                        label={t("auditLog.toDate")}
                                        type="date"
                                        variant="outlined"
                                        value={toDate}
                                        onChange={handleToDateChange}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            htmlInput: {
                                                min: fromDate || undefined,
                                                "aria-label": t("auditLog.toDate"),
                                            },
                                        }}
                                    />
                                </Grid2>

                                {/* Event Checkbox Multi-Select */}
                                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_SELECT_CLASS}
                                        select
                                        label={t("auditLog.event")}
                                        variant="outlined"
                                        value={selectedEvents}
                                        onChange={handleEventChange}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            select: {
                                                multiple: true,
                                                displayEmpty: true,
                                                renderValue: renderSelectedEvents,
                                            },
                                        }}
                                    >
                                        {eventList.map((item) => {
                                            const eventCode = getEventCode(item);
                                            const eventLabel = getEventLabel(item);
                                            return (
                                                <MenuItem key={eventCode} value={eventCode}>
                                                    <Checkbox checked={selectedEvents.includes(eventCode)} />
                                                    <ListItemText primary={eventLabel} />
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Grid2>

                                {/* Email Input */}
                                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_INPUT_CLASS}
                                        label={t("auditLog.email")}
                                        placeholder={t("auditLog.enterEmail")}
                                        variant="outlined"
                                        value={emailText}
                                        onChange={handleEmailChange}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                        }}
                                    />
                                </Grid2>

                                {/* Search & Reset Buttons */}
                                <Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
                                    <Box className="client-filter-actions audit-filter-actions">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className="margin-right-s"
                                            onClick={searchBtnCall}
                                        >
                                            {t("common.search")}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={resetSearch}
                                        >
                                            {t("common.reset")}
                                        </Button>
                                    </Box>
                                </Grid2>
                            </Grid2>

                            {/* Record Info & Download */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                }}
                            >
                                <Typography className="table-rec" variant="body2">
                                    {t("common.total")} {totalItems} {t("common.recordsFound")}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    sx={{ borderColor: "#25408f" }}
                                    onClick={downloadBtnCall}
                                >
                                    {t("common.download")}
                                </Button>
                            </Box>

                            {/* Data Table */}
                            <TableContainer
                                component={Paper}
                                className="list-height-aud border client-table-container"
                            >
                                <Table
                                    sx={{
                                        "--label-1": `"${t("auditLog.event")}"`,
                                        "--label-2": `"${t("auditLog.details")}"`,
                                        "--label-3": `"${t("auditLog.changedOn")}"`,
                                        "--label-4": `"${t("auditLog.changedBy")}"`,
                                        "--label-5": `"${t("auditLog.userType")}"`,
                                        "--label-6": `"${t("auditLog.dashboard")}"`,
                                        "--label-7": `"${t("auditLog.group")}"`,
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {AUDIT_LOG_COLUMNS.map((col) => (
                                                <TableCell
                                                    key={col.id}
                                                    sortDirection={sortBy === col.id ? sortOrder : false}
                                                >
                                                    <TableSortLabel
                                                        active={sortBy === col.id}
                                                        direction={sortBy === col.id ? sortOrder : "asc"}
                                                        onClick={() => handleRequestSort(col.id)}
                                                    >
                                                        {t(col.labelKey)}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedAuditLog.length > 0 ? (
                                            paginatedAuditLog.map((auditLog) => (
                                                <TableRow key={getAuditLogRowKey(auditLog)}>
                                                    <TableCell>{auditLog.eventName}</TableCell>
                                                    <TableCell>{auditLog.details}</TableCell>
                                                    <TableCell>
                                                        {formatChangedOnDate(
                                                            auditLog.changedOn,
                                                            t("common.invalidDate")
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{auditLog.changedBy}</TableCell>
                                                    <TableCell>{auditLog.userType}</TableCell>
                                                    <TableCell>{auditLog.dashboard}</TableCell>
                                                    <TableCell>{auditLog.group}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={AUDIT_LOG_COLUMNS.length}>
                                                    {t("common.noRecordsFound")}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            <CommonPagination
                                totalItems={totalItems}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                            />
                        </Box>
                    </section>
                </Box>
            </Box>
            {ToastComponent}
        </>
    );
};

export default AuditLog;
